"use client";

/**
 * DataFeedReadoutWidget — Live / Realtime / Delayed / As-of strip.
 * Cross-cutting honesty for any desk surface.
 */
import React, { useMemo } from "react";
import type { DeskFeedStatus } from "./lib/types";
import { STALE_MS } from "./lib/deskFormat";
import local from "./DataFeedReadoutWidget.module.css";

export interface DataFeedReadoutWidgetProps {
  status: DeskFeedStatus;
  className?: string;
}

function mapHttpLabel(s: DeskFeedStatus["http"]): string {
  if (s === "connected") return "Live";
  if (s === "connecting") return "Connecting…";
  if (s === "error") return "Data error";
  return "Data offline";
}

function mapRtLabel(s: DeskFeedStatus["realtime"]): string {
  if (s === "connected") return "Realtime";
  if (s === "connecting") return "Realtime…";
  if (s === "error") return "Realtime error";
  return "Realtime off";
}

export function DataFeedReadoutWidget({
  status,
  className = "",
}: DataFeedReadoutWidgetProps) {
  const staleMs = status.staleMs ?? STALE_MS;
  const ageMs = status.lastRefreshed
    ? Date.now() - status.lastRefreshed.getTime()
    : null;

  const delayed =
    status.realtime !== "connected" &&
    ageMs != null &&
    ageMs > staleMs &&
    status.http === "connected";

  const refreshedLabel = status.lastRefreshed
    ? status.lastRefreshed.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : null;

  const items = useMemo(() => {
    const list: { key: string; label: string; dataStatus: string; title: string }[] =
      [
        {
          key: "http",
          label: mapHttpLabel(status.http),
          dataStatus: status.http === "connected" ? "connected" : status.http,
          title: `HTTP: ${status.http}`,
        },
      ];
    list.push({
      key: "rt",
      label: mapRtLabel(status.realtime),
      dataStatus:
        status.realtime === "connected" ? "connected" : status.realtime,
      title: `Realtime: ${status.realtime}`,
    });
    if (delayed) {
      list.push({
        key: "delayed",
        label: "Delayed",
        dataStatus: "warning",
        title: "Realtime off and last refresh older than stale threshold",
      });
    }
    return list;
  }, [status.http, status.realtime, delayed]);

  return (
    <div
      className={`${local.readout} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {items.map((item, i) => (
        <React.Fragment key={item.key}>
          {i > 0 ? (
            <span className={local.sep} aria-hidden>
              ·
            </span>
          ) : null}
          <span
            className={local.item}
            data-status={item.dataStatus}
            title={item.title}
          >
            {item.label}
          </span>
        </React.Fragment>
      ))}
      {refreshedLabel ? (
        <>
          <span className={local.sep} aria-hidden>
            ·
          </span>
          <span className={local.item}>
            <time dateTime={status.lastRefreshed?.toISOString()}>
              {refreshedLabel}
            </time>
          </span>
        </>
      ) : null}
    </div>
  );
}
